import React, { useEffect, useState } from 'react';
import { areasAPI } from '../../utils/api';
import { formatPresenceDuration as formatDuration, getPresenceTodayDate, AreaPresenceSummary } from '../../utils/presence';
import PresenceStatus from './PresenceStatus';
import '../DetectionsStatsModal/DetectionsStatsModal.css';
import './PresenceStatsModal.css';

interface Props {
  areaId: number;
  areaName: string;
  onClose: () => void;
}

const PresenceStatsModal: React.FC<Props> = ({ areaId, areaName, onClose }) => {
  const [date, setDate] = useState(getPresenceTodayDate);
  const [summary, setSummary] = useState<AreaPresenceSummary | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    setSummary(null);
    setError('');
    if (!date) { setLoading(false); return; }
    setLoading(true);
    let inFlight = false;
    const load = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const response = await areasAPI.getPresenceSummary(areaId, date);
        if (!response.success || !response.data || !Array.isArray(response.data.cameras)
          || response.data.area_id !== areaId || response.data.date !== date
          || typeof response.data.absences !== 'number' || typeof response.data.total_absence_sec !== 'number') {
          throw new Error(response.message || 'Не удалось загрузить статистику присутствия участка');
        }
        if (!cancelled) { setSummary(response.data); setError(''); }
      } catch (err) {
        if (!cancelled) {
          setSummary(null);
          setError(err instanceof Error ? err.message : 'Не удалось загрузить статистику присутствия участка');
        }
      } finally {
        if (!cancelled) setLoading(false);
        inFlight = false;
      }
    };
    void load();
    const timer = window.setInterval(() => {
      if (date === getPresenceTodayDate() && document.visibilityState === 'visible') void load();
    }, 10_000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [areaId, date, refresh]);

  return <div className="area-modal det-stats-modal" role="dialog" aria-modal="true" aria-labelledby="presence-stats-title">
    <div className="area-modal__backdrop" onClick={onClose} />
    <div className="area-modal__dialog det-stats-modal__dialog">
      <header className="area-modal__header">
        <div><h2 id="presence-stats-title" className="area-modal__title">Статистика присутствия</h2><p className="det-stats__subtitle">{areaName} · камеры с контролем присутствия</p></div>
        <button type="button" className="area-modal__close" aria-label="Закрыть" onClick={onClose}>×</button>
      </header>
      <div className="det-stats-modal__body">
        <div className="presence-stats__filters">
          <label>Дата <input className="ui-input" type="date" value={date} onChange={(event) => setDate(event.target.value)} /></label>
          <button type="button" className="ui-btn ui-btn--outline ui-btn--sm" disabled={loading || !date} onClick={() => setRefresh((value) => value + 1)}>Обновить</button>
        </div>
        <p className="det-stats__subtitle">Дневные итоги и текущий статус рабочих мест. Повторные снимки не считаются отдельными периодами.{date === getPresenceTodayDate() && ' Обновляется каждые 10 секунд.'}</p>
        {loading ? <div className="det-stats__loading" role="status">Загрузка статистики…</div> : !date ? <div className="det-stats__empty">Выберите дату</div> : <>
          {error && <p className="det-stats__error" role="alert">{error}</p>}
          {summary && summary.cameras.length > 0 && <div className="det-stats__cards presence-stats__totals">
            <div className="det-stats__card det-stats__card--day"><span className="det-stats__card-label">Периодов отсутствия</span><span className="det-stats__card-value">{summary.absences}</span></div>
            <div className="det-stats__card"><span className="det-stats__card-label">Суммарно по рабочим местам</span><span className="presence-stats__duration">{formatDuration(summary.total_absence_sec)}</span></div>
          </div>}
          {summary?.cameras.length === 0 && <div className="det-stats__empty">На участке нет камер с контролем присутствия</div>}
          {summary?.cameras.map((camera) => <section className="det-stats__section" key={camera.camera_id}>
            <h3>{camera.name}</h3>
            {camera.zones.length === 0 ? <p className="det-stats__empty">Нет данных за выбранный день</p> : <div className="ui-table-wrap"><table className="ui-table presence-stats__table">
              <thead><tr><th>Рабочее место</th><th>Статус сейчас</th><th>Отсутствий</th><th>Общее отсутствие</th></tr></thead>
              <tbody>{camera.zones.map((zone, index) => <tr key={index}><td>{zone.zone_name ?? 'Весь кадр'}</td><td><PresenceStatus zone={zone} /></td><td>{zone.absences}</td><td>{formatDuration(zone.total_absence_sec)}</td></tr>)}</tbody>
            </table></div>}
          </section>)}
        </>}
      </div>
      <footer className="area-modal__footer"><button type="button" className="area-modal__submit area-modal__submit--secondary" onClick={onClose}>Закрыть</button></footer>
    </div>
  </div>;
};

export default PresenceStatsModal;
